import {
  Add as AddIcon,
  Dangerous as DangerousIcon,
  Edit as EditIcon,
  Inventory as InventoryIcon,
  MeetingRoom as RoomIcon,
  Store as StoreIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Paper,
  Button,
  Chip,
  CircularProgress,
  Stack,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useSnackbar } from "notistack";
import React, { useCallback, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import {
  AdminDialog,
  AdminEmptyState,
  AdminFilterSummary,
  AdminTableActions,
  AdminTableFrame,
  PageShell,
} from "@/components/common";
import { LoadingRing } from "@/components/common/LoadingRing";
import { LocalizedTablePagination } from "@/components/common/LocalizedTablePagination";
import MuiAutocompleteMultiSelect from "@/components/common/MuiAutocompleteMultiSelect";
import { RecordFields, RecordList } from "@/components/common/RecordList";
import { RecordSortControl } from "@/components/common/RecordSortControl";
import { SortableTableCell } from "@/components/common/SortableTableCell";
import { getApiConfig } from "@/config/api.config";
import { useAuth } from "@/contexts/AuthContext";
import { useTableSorting } from "@/hooks/useTableSorting";
import { Customer, StoreService, Store as StoreType } from "@/services/features/StoreService";
import { formatInteger } from "@/utils/number";

const inventoryApiUrl = getApiConfig().inventory;
const showDeactivateStore = false;

const Stores: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { isLoading: authLoading } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const storeService = StoreService.getInstance();

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [stores, setStores] = useState<StoreType[]>([]);
  const [currentCustomer, setCurrentCustomer] = useState<Customer | null>(null);
  const [storeToDisable, setStoreToDisable] = useState<StoreType | null>(null);
  const [disableConfirmation, setDisableConfirmation] = useState("");
  const [disableError, setDisableError] = useState<string | null>(null);
  const selectedCustomerId = searchParams.get("customerId");
  const { sortColumn, sortDirection, handleSort, sortedItems } = useTableSorting(
    stores,
    (store, column) =>
      ({
        id: store.id,
        externalId: store.externalId,
        name: store.name,
        locale: store.locale,
        currency: store.currencyCode,
        timezone: store.timezone,
      })[column],
    "id" as "id" | "externalId" | "name" | "locale" | "currency" | "timezone",
  );

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const getCustomers = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await storeService.getCustomers();
      if (response.data) setCustomers(response.data);
    } catch {
      // Preserve the existing empty-state behavior when lookup loading fails.
    } finally {
      setIsLoading(false);
    }
  }, [storeService]);

  useEffect(() => {
    document.title = t("management.stores.stores.documentTitle");
  }, [t]);
  useEffect(() => {
    void getCustomers();
  }, [getCustomers]);

  const getStores = useCallback(
    async (customerId: number) => {
      setIsLoading(true);
      try {
        const response = await storeService.getStoresByCustomer(customerId);
        if (response.data) {
          setStores(response.data);
        }
      } catch {
      } finally {
        setIsLoading(false);
      }
    },
    [storeService],
  );

  useEffect(() => {
    if (!selectedCustomerId || customers.length === 0) return;

    const customer = customers.find((entry) => entry.id === Number(selectedCustomerId));
    if (customer && customer.id !== currentCustomer?.id) {
      setCurrentCustomer(customer);
      setPage(0);
      void getStores(customer.id);
    }
  }, [currentCustomer?.id, customers, getStores, selectedCustomerId]);

  const handleCustomerChange = (newValue: Customer | null) => {
    setCurrentCustomer(newValue);
    setPage(0);
    setStores([]);
    setSearchParams((currentParams) => {
      const nextParams = new URLSearchParams(currentParams);

      if (newValue) {
        nextParams.set("customerId", String(newValue.id));
      } else {
        nextParams.delete("customerId");
      }

      return nextParams;
    });

    if (newValue) {
      void getStores(newValue.id);
    }
  };

  const goToAdd = () => {
    if (currentCustomer) {
      navigate(`/admin/organization/stores/add/${currentCustomer.id}/${currentCustomer.name}`, {
        state: { returnTo: location.pathname + location.search },
      });
    }
  };

  const goToDetails = (storeId: number) => {
    void navigate(`/admin/organization/stores/${storeId}`, {
      state: { returnTo: `${location.pathname}${location.search}` },
    });
  };

  const goToEdit = (storeId: number) => {
    navigate(`/admin/organization/stores/edit/${storeId}`, {
      state: { returnTo: `${location.pathname}${location.search}` },
    });
  };

  const goToEditChangingRooms = (storeId: number) => {
    navigate(`/admin/organization/stores/edit-rooms/${storeId}`, {
      state: { returnTo: location.pathname + location.search },
    });
  };

  const openInventory = (storeId: number) => {
    window.open(`${inventoryApiUrl}/sample/${storeId}`, "_blank", "noopener,noreferrer");
  };

  const openDisableDialog = (store: StoreType) => {
    setStoreToDisable(store);
    setDisableConfirmation("");
    setDisableError(null);
  };

  const closeDisableDialog = () => {
    if (isLoading) return;
    setStoreToDisable(null);
    setDisableConfirmation("");
    setDisableError(null);
  };

  const handleDisableStore = async () => {
    if (!storeToDisable || disableConfirmation.trim() !== String(storeToDisable.id)) return;

    setIsLoading(true);
    setDisableError(null);
    const response = await storeService.disableStoreById(storeToDisable.id);

    if (response.success) {
      enqueueSnackbar(t("management.stores.stores.notifications.storeDisabled"), {
        variant: "success",
      });
      setStoreToDisable(null);
      setDisableConfirmation("");
      if (currentCustomer) {
        await getStores(currentCustomer.id);
      }
    } else {
      setDisableError(response.error || t("management.stores.stores.errors.failedDisableStore"));
    }

    setIsLoading(false);
  };

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const currentPage = Math.min(page, Math.max(0, Math.ceil(sortedItems.length / rowsPerPage) - 1));
  const displayedStores = sortedItems.slice(
    currentPage * rowsPerPage,
    (currentPage + 1) * rowsPerPage,
  );
  const changeSort = (column: typeof sortColumn) => {
    setPage(0);
    handleSort(column);
  };
  const sortOptions = [
    { value: "id" as const, label: t("management.stores.stores.fields.id") },
    { value: "externalId" as const, label: t("management.stores.stores.fields.externalId") },
    { value: "name" as const, label: t("management.stores.stores.fields.storeName") },
    { value: "locale" as const, label: t("management.stores.stores.fields.locale") },
    { value: "currency" as const, label: t("management.stores.stores.fields.currency") },
    { value: "timezone" as const, label: t("management.stores.stores.fields.timezone") },
  ];
  const renderPagination = (position: "top" | "bottom" = "bottom") => (
    <LocalizedTablePagination
      data-pagination-position={position}
      component="div"
      count={sortedItems.length}
      page={currentPage}
      rowsPerPage={rowsPerPage}
      rowsPerPageOptions={[25, 50, 100]}
      onPageChange={(_, next) => setPage(next)}
      onRowsPerPageChange={(event) => {
        setRowsPerPage(Number(event.target.value));
        setPage(0);
      }}
    />
  );
  const renderMobileView = () => (
    <Paper sx={{ p: 2, minWidth: 0 }}>
      <RecordSortControl
        value={sortColumn}
        direction={sortDirection}
        options={sortOptions}
        onSort={changeSort}
      />
      {renderPagination("top")}
      <RecordList
        items={displayedStores}
        getItemId={(store) => store.id}
        loading={isLoading}
        resetKey={`${currentCustomer?.id}:${currentPage}:${rowsPerPage}:${sortColumn}:${sortDirection}`}
        renderTitle={(store) => store.name}
        renderSummary={(store) => (
          <RecordFields
            fields={[
              {
                label: t("management.stores.stores.fields.id"),
                value: formatInteger(store.id, i18n.language),
              },
              {
                label: t("management.stores.stores.fields.externalId"),
                value: store.externalId || t("management.stores.stores.empty.unset"),
              },
            ]}
          />
        )}
        renderActions={(store) => (
          <AdminTableActions
            actions={[
              {
                icon: <VisibilityIcon />,
                hint: t("commandCenter.actions.view"),
                action: () => goToDetails(store.id),
                disabled: isLoading,
              },
              {
                icon: <EditIcon />,
                hint: t("management.stores.stores.actions.edit"),
                action: () => goToEdit(store.id),
                disabled: isLoading,
              },
              {
                icon: <RoomIcon />,
                hint: t("management.stores.stores.actions.rooms"),
                action: () => goToEditChangingRooms(store.id),
                disabled: isLoading,
              },
              {
                icon: <InventoryIcon />,
                hint: t("management.stores.stores.actions.inventory"),
                action: () => openInventory(store.id),
                disabled: isLoading,
              },
              ...(showDeactivateStore && store.active
                ? [
                    {
                      icon: <DangerousIcon />,
                      hint: t("management.stores.stores.actions.disable"),
                      action: () => openDisableDialog(store),
                      disabled: isLoading,
                      color: "error" as const,
                    },
                  ]
                : []),
            ]}
          />
        )}
        renderDetails={(store) => (
          <>
            <RecordFields
              fields={[
                {
                  label: t("management.stores.stores.fields.locale"),
                  value: store.locale || t("management.stores.stores.empty.unset"),
                },
                {
                  label: t("management.stores.stores.fields.currency"),
                  value: store.currencyCode || t("management.stores.stores.empty.unset"),
                },
                { label: t("management.stores.stores.fields.timezone"), value: store.timezone },
              ]}
            />
          </>
        )}
        emptyState={
          <AdminEmptyState
            icon={<StoreIcon sx={{ fontSize: 40 }} />}
            text={
              currentCustomer
                ? t("management.stores.stores.empty.noStores")
                : t("management.stores.stores.empty.selectCustomer")
            }
          />
        }
      />
      {renderPagination()}
    </Paper>
  );

  if (authLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <PageShell
      breadcrumbs="admin"
      title={t("management.stores.stores.title")}
      icon={<StoreIcon />}
      titleAccessory={<LoadingRing loading={isLoading} />}
      headerActions={
        <Button
          variant="contained"
          onClick={goToAdd}
          disabled={!currentCustomer || isLoading}
          startIcon={<AddIcon />}
        >
          {t("management.stores.stores.actions.addNewStore")}
        </Button>
      }
    >
      <AdminFilterSummary
        filter={
          <Box sx={{ position: "relative" }}>
            <MuiAutocompleteMultiSelect
              label={t("management.stores.stores.sections.selectCustomer")}
              options={customers}
              getOptionLabel={(option) => option.name}
              getOptionKey={(option) => option.id}
              value={currentCustomer ? [currentCustomer] : []}
              onChange={(next) => handleCustomerChange(next[0] ?? null)}
              loading={isLoading}
              disabled={isLoading}
            />
          </Box>
        }
        count={stores.length}
        countLabel={t("management.stores.stores.fields.totalStores")}
        icon={<StoreIcon color="primary" sx={{ fontSize: 40 }} />}
      />

      {/* Stores Table/Cards */}
      <Box>
        {isMobile ? (
          renderMobileView()
        ) : (
          <Box>
            {renderPagination("top")}
            <AdminTableFrame>
              <TableHead>
                <TableRow>
                  <SortableTableCell
                    activeColumn={sortColumn}
                    column="id"
                    direction={sortDirection}
                    onSort={changeSort}
                  >
                    {t("management.stores.stores.fields.id")}
                  </SortableTableCell>
                  <SortableTableCell
                    activeColumn={sortColumn}
                    column="externalId"
                    direction={sortDirection}
                    onSort={changeSort}
                  >
                    {t("management.stores.stores.fields.externalId")}
                  </SortableTableCell>
                  <SortableTableCell
                    activeColumn={sortColumn}
                    column="name"
                    direction={sortDirection}
                    onSort={changeSort}
                  >
                    {t("management.stores.stores.fields.storeName")}
                  </SortableTableCell>
                  <SortableTableCell
                    activeColumn={sortColumn}
                    column="locale"
                    direction={sortDirection}
                    onSort={changeSort}
                  >
                    {t("management.stores.stores.fields.locale")}
                  </SortableTableCell>
                  <SortableTableCell
                    activeColumn={sortColumn}
                    column="currency"
                    direction={sortDirection}
                    onSort={changeSort}
                  >
                    {t("management.stores.stores.fields.currency")}
                  </SortableTableCell>
                  <SortableTableCell
                    activeColumn={sortColumn}
                    column="timezone"
                    direction={sortDirection}
                    onSort={changeSort}
                  >
                    {t("management.stores.stores.fields.timezone")}
                  </SortableTableCell>
                  <TableCell align="right">
                    {t("management.stores.stores.fields.actions")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {stores.length > 0 ? (
                  displayedStores.map((store) => (
                    <TableRow
                      key={store.id}
                      hover
                      role="button"
                      tabIndex={0}
                      onClick={() => goToDetails(store.id)}
                      onKeyDown={(event) => {
                        if (event.target !== event.currentTarget) {
                          return;
                        }

                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          goToDetails(store.id);
                        }
                      }}
                      sx={{
                        cursor: "pointer",
                      }}
                    >
                      <TableCell>{store.id}</TableCell>
                      <TableCell>
                        {store.externalId || t("management.stores.stores.empty.unset")}
                      </TableCell>
                      <TableCell>{store.name}</TableCell>
                      <TableCell>
                        {store.locale ? (
                          <Chip
                            label={store.locale}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        ) : (
                          t("management.stores.stores.empty.unset")
                        )}
                      </TableCell>
                      <TableCell>
                        {store.currencyCode ? (
                          <Chip
                            label={store.currencyCode}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        ) : (
                          t("management.stores.stores.empty.unset")
                        )}
                      </TableCell>
                      <TableCell>{store.timezone}</TableCell>
                      <TableCell align="right">
                        <AdminTableActions
                          actions={[
                            {
                              icon: <VisibilityIcon />,
                              hint: t("commandCenter.actions.view"),
                              action: () => goToDetails(store.id),
                              disabled: isLoading,
                            },
                            {
                              icon: <EditIcon />,
                              hint: t("management.stores.stores.tooltips.editStore"),
                              action: () => goToEdit(store.id),
                              disabled: isLoading,
                            },
                            {
                              icon: <RoomIcon />,
                              hint: t("management.stores.stores.tooltips.editRooms"),
                              action: () => goToEditChangingRooms(store.id),
                              disabled: isLoading,
                            },
                            {
                              icon: <InventoryIcon />,
                              hint: t("management.stores.stores.tooltips.viewInventory"),
                              action: () => openInventory(store.id),
                              disabled: isLoading,
                            },
                            ...(showDeactivateStore && store.active
                              ? [
                                  {
                                    icon: <DangerousIcon />,
                                    hint: t("management.stores.stores.tooltips.disableStore"),
                                    action: () => openDisableDialog(store),
                                    disabled: isLoading,
                                    color: "error" as const,
                                  },
                                ]
                              : []),
                          ]}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <AdminEmptyState
                        icon={<StoreIcon sx={{ fontSize: 40 }} />}
                        text={
                          currentCustomer
                            ? t("management.stores.stores.empty.noStores")
                            : t("management.stores.stores.empty.selectCustomer")
                        }
                      />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </AdminTableFrame>
            {renderPagination()}
          </Box>
        )}
      </Box>
      <AdminDialog
        open={Boolean(storeToDisable)}
        onClose={closeDisableDialog}
        title={t("management.stores.stores.disableDialog.title")}
        icon={<DangerousIcon />}
        error={disableError}
        cancelAction={{
          label: t("common.cancel"),
          onClick: closeDisableDialog,
          disabled: isLoading,
        }}
        primaryAction={{
          label: t("management.stores.stores.actions.disable"),
          onClick: () => void handleDisableStore(),
          color: "error",
          loading: isLoading,
          disabled: !storeToDisable || disableConfirmation.trim() !== String(storeToDisable.id),
        }}
      >
        <Stack spacing={2}>
          <Alert severity="warning">
            <Trans
              i18nKey="management.stores.stores.deactivateDialog.warning"
              values={{ storeName: storeToDisable?.name }}
              components={{
                storeName: <Box component="span" sx={{ fontStyle: "italic" }} />,
              }}
            />
          </Alert>
          <Typography>
            <Trans
              i18nKey="management.stores.stores.deactivateDialog.confirmation"
              values={{ storeId: storeToDisable?.id }}
              components={{ storeId: <Box component="span" sx={{ fontWeight: 700 }} /> }}
            />
          </Typography>
          <TextField
            fullWidth
            label={t("management.stores.stores.disableDialog.storeIdLabel")}
            value={disableConfirmation}
            onChange={(event) => setDisableConfirmation(event.target.value)}
            disabled={isLoading}
            inputProps={{ inputMode: "numeric" }}
          />
        </Stack>
      </AdminDialog>
    </PageShell>
  );
};

export default Stores;
